// Vetrina del design system: mostra i nomi tecnici dei token e delle varianti come testo.
// Per questo è l'unico file escluso dalla regola `react/jsx-no-literals` (Task 10);
// i titoli e i messaggi rivolti all'utente arrivano comunque dai cataloghi.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { healthQuery } from "../api/queries";
import { AboutBox } from "../design/components/AboutBox";
import { PublicShell } from "../design/components/PublicShell";
import { Button } from "../design/components/Button";
import { Card } from "../design/components/Card";
import { Checkbox } from "../design/components/Checkbox";
import { Chip } from "../design/components/Chip";
import { DataTable } from "../design/components/DataTable";
import { Dialog } from "../design/components/Dialog";
import { Drawer } from "../design/components/Drawer";
import { NameFields } from "../design/components/NameFields";
import { PageHeader } from "../design/components/PageHeader";
import { Reason } from "../design/components/Reason";
import { SaveBar } from "../design/components/SaveBar";
import { Segmented } from "../design/components/Segmented";
import { Select } from "../design/components/Select";
import { StatusPill } from "../design/components/StatusPill";
import { TextArea } from "../design/components/TextArea";
import { TextField } from "../design/components/TextField";
import { useToast } from "../design/components/Toast";
import type { Tone } from "../design/components/tone";
import { breakpoints, tokenGroups, type TokenPreview } from "../design/manifest";
import { useT } from "../i18n/locale";
import "./styleguide.css";

const buttonVariants = ["primary", "secondary", "ghost"] as const;

const tones: readonly Tone[] = ["neutral", "accent", "success", "warning", "danger"];

const roleOptions = [
  { value: "user", label: "User" },
  { value: "super-user", label: "Super user" },
  { value: "admin", label: "Admin" },
  { value: "super-admin", label: "Super admin", disabled: true },
];

// Anteprima di un token: ogni gruppo mostra il suo con lo stile del token stesso, mai con valori copiati.
function TokenPreviewBox({ kind, name }: { kind: TokenPreview; name: string }) {
  const v = `var(${name})`;
  switch (kind) {
    case "color":
      return <span className="g-styleguide__chip" style={{ background: v }} aria-hidden="true" />;
    case "text":
      return name.startsWith("--text-") ? <span style={{ fontSize: v }}>Aa</span> : <span style={{ lineHeight: v }}>Aa Bb</span>;
    case "font":
      return <span style={{ fontFamily: v }}>Aa Bb 123</span>;
    case "space":
      return <span className="g-styleguide__chip" style={{ width: v, height: v, background: "var(--color-accent)" }} aria-hidden="true" />;
    case "radius":
      return <span className="g-styleguide__chip" style={{ borderRadius: v, width: "var(--space-10)" }} aria-hidden="true" />;
    case "shadow":
      return <span className="g-styleguide__chip" style={{ boxShadow: v }} aria-hidden="true" />;
    case "size":
      return <span className="g-styleguide__chip" style={name.startsWith("--control") ? { height: v } : { width: `min(${v}, 100%)`, height: "var(--space-2)" }} aria-hidden="true" />;
    case "layer":
      return <span aria-hidden="true" />;
  }
}

function ServerStatus() {
  const t = useT();
  const health = useQuery(healthQuery);
  const state = health.isPending ? "pending" : health.isError ? "error" : "ok";
  return (
    <p className="g-styleguide__status" data-state={state} role="status">
      {health.isPending
        ? "—"
        : health.isError
          ? t("styleguide.serverUnavailable")
          : t("styleguide.serverVersion", { version: health.data.version })}
    </p>
  );
}

// Le finestre del sistema: una al centro (due larghezze) e un pannello a destra.
function DialogDemos() {
  const [open, setOpen] = useState<"normal" | "wide" | "drawer" | null>(null);
  const close = () => setOpen(null);
  return (
    <>
      <div className="g-styleguide__row">
        <Button onClick={() => setOpen("normal")}>Dialog</Button>
        <Button onClick={() => setOpen("wide")}>Dialog / wide</Button>
        <Button onClick={() => setOpen("drawer")}>Drawer</Button>
      </div>
      <Dialog
        open={open === "normal"}
        title="Sospendi Davide Russo"
        onClose={close}
        footer={
          <>
            <Button onClick={close}>Annulla</Button>
            <Button variant="primary" onClick={close}>Sospendi</Button>
          </>
        }
      >
        <p>Non potrà più entrare finché non lo riattivi. I suoi Lead restano dove sono.</p>
        <TextField id="styleguide-dialog-reason" label="Motivo (facoltativo)" />
      </Dialog>
      <Dialog
        open={open === "wide"}
        title="Il WhatsApp di Luca Bianchi"
        size="wide"
        onClose={close}
        footer={
          <>
            <Button onClick={close}>Annulla</Button>
            <Button variant="primary" onClick={close}>Salva</Button>
          </>
        }
      >
        <Select id="styleguide-dialog-kind" label="Quale WhatsApp usa" value="ufficiale" onChange={() => {}} options={[{ value: "ufficiale", label: "WhatsApp ufficiale di Meta" }]} />
        <Checkbox id="styleguide-dialog-copy" label="Avvisa Luca" checked onChange={() => {}} hint="Riceve un messaggio con il nuovo numero." />
      </Dialog>
      <Drawer
        open={open === "drawer"}
        title="Il tuo account"
        onClose={close}
        footer={<Button onClick={close}>Esci</Button>}
      >
        <p>Renato Bianchi · Super admin di Gioia365</p>
        <Select id="styleguide-drawer-lang" label="Lingua" value="it" onChange={() => {}} options={[{ value: "it", label: "Italiano" }, { value: "en", label: "Inglese" }]} />
      </Drawer>
    </>
  );
}

function FieldDemos() {
  const [role, setRole] = useState("user");
  const [calls, setCalls] = useState(true);
  const [reports, setReports] = useState(false);
  return (
    <div className="g-styleguide__grid">
      <Select id="styleguide-select" label="Select" value={role} onChange={setRole} options={roleOptions} hint="Il cambio vale subito." />
      <Select id="styleguide-select-error" label="Select / error" value="" onChange={() => {}} options={[{ value: "", label: "Scegli un ruolo" }, ...roleOptions]} error="Scegli un ruolo." />
      <Select id="styleguide-select-disabled" label="Select / disabled" value="admin" onChange={() => {}} options={roleOptions} disabled />
      <div className="g-stack">
        <Checkbox id="styleguide-check" label="Checkbox" checked={calls} onChange={setCalls} hint="Vale per tutte le liste." />
        <Checkbox id="styleguide-check-off" label="Checkbox / unchecked" checked={reports} onChange={setReports} />
        <Checkbox id="styleguide-check-disabled" label="Checkbox / disabled" checked disabled onChange={() => {}} describedBy="styleguide-check-why" />
        <Reason id="styleguide-check-why">Lo decide Gioia365: non lo puoi cambiare tu.</Reason>
      </div>
    </div>
  );
}

function TableDemo() {
  return (
    <DataTable caption="Il team dell'Academy">
      <thead>
        <tr>
          <th scope="col">Persona</th>
          <th scope="col">Ruolo</th>
          <th scope="col">Stato</th>
          <th scope="col">Numero per chiamare</th>
          <th scope="col">
            <span className="g-visually-hidden">Azione</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row">
            Renato Bianchi <Chip>titolare</Chip> <Chip tone="accent">sei tu</Chip>
            <span className="g-table__sub">renato@example.test</span>
          </th>
          <td>Super admin</td>
          <td><StatusPill tone="success" icon="check">Attivo</StatusPill></td>
          <td>+39 02 0000 0001</td>
          <td><Chip icon="lock">protetto</Chip></td>
        </tr>
        <tr>
          <th scope="row">
            Sara Conti
            <span className="g-table__sub">sara@example.test</span>
          </th>
          <td>Admin</td>
          <td><StatusPill tone="warning" icon="clock">Invitato</StatusPill></td>
          <td>—</td>
          <td><Button>Gestisci</Button></td>
        </tr>
        <tr>
          <th scope="row">
            Davide Russo
            <span className="g-table__sub">davide@example.test</span>
          </th>
          <td>User</td>
          <td><StatusPill tone="neutral" icon="minus">Sospeso</StatusPill></td>
          <td>+39 02 0000 0002</td>
          <td><Button>Gestisci</Button></td>
        </tr>
        <tr>
          <th scope="row">
            Elisa Fabbri
            <span className="g-table__sub">elisa@example.test</span>
          </th>
          <td>Super user</td>
          <td><StatusPill tone="danger" icon="alert">Invito scaduto</StatusPill></td>
          <td>—</td>
          <td><Button>Gestisci</Button></td>
        </tr>
      </tbody>
    </DataTable>
  );
}

// Ogni tono con l'icona che di solito lo accompagna nelle tabelle.
const pillDemos = [
  { tone: "neutral", icon: "minus" },
  { tone: "accent", icon: "lock" },
  { tone: "success", icon: "check" },
  { tone: "warning", icon: "clock" },
  { tone: "danger", icon: "alert" },
] as const;

function ToastButtons() {
  const { show } = useToast();
  return (
    <div className="g-styleguide__row">
      {tones.map((tone) => (
        <Button key={tone} onClick={() => show(`Ruolo «Setter» salvato (${tone}).`, tone)}>{`toast / ${tone}`}</Button>
      ))}
    </div>
  );
}

function SegmentedDemo() {
  const [view, setView] = useState("users");
  return (
    <Segmented
      label="Segmented"
      value={view}
      onChange={setView}
      options={[
        { value: "users", label: "Utenti" },
        { value: "roles", label: "Ruoli" },
        { value: "platform", label: "Per tutte le aziende" },
      ]}
    />
  );
}

export function StyleguidePage() {
  const t = useT();
  return (
    <PublicShell width="wide">
      <Card className="g-stack">
        <h1>{t("styleguide.title")}</h1>

        <section className="g-styleguide__section" aria-labelledby="styleguide-server">
          <h2 id="styleguide-server">{t("styleguide.serverStatus")}</h2>
          <ServerStatus />
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-tokens">
          <h2 id="styleguide-tokens">{t("styleguide.tokens")}</h2>
          {tokenGroups.map((group) => (
            <div key={group.id} className="g-styleguide__section">
              <h3>{group.title}</h3>
              <ul className="g-styleguide__tokens">
                {group.tokens.map((token) => (
                  <li key={token.name} className="g-styleguide__token">
                    <TokenPreviewBox kind={group.preview} name={token.name} />
                    <code>{token.name}</code>
                    <span>{token.use}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-breakpoints">
          <h2 id="styleguide-breakpoints">{t("styleguide.breakpoints")}</h2>
          <ul className="g-styleguide__tokens">
            {breakpoints.map((point) => (
              <li key={point.px} className="g-styleguide__token">
                <code>{`${point.px} px`}</code>
                <span>{point.use}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-components">
          <h2 id="styleguide-components">{t("styleguide.components")}</h2>
          <div className="g-styleguide__row">
            {buttonVariants.map((variant) => (
              <Button key={variant} variant={variant}>{variant}</Button>
            ))}
          </div>
          <div className="g-styleguide__row">
            {buttonVariants.map((variant) => (
              <Button key={variant} variant={variant} disabled>{`${variant} / disabled`}</Button>
            ))}
          </div>
          <div className="g-stack">
            <TextField id="styleguide-field" label="TextField" placeholder="default" readOnly />
            <TextField id="styleguide-field-error" label="TextField / error" error="error" defaultValue="—" readOnly />
            <TextArea id="styleguide-textarea" label="TextArea" defaultValue={"—\n—"} readOnly />
            <NameFields id="styleguide-name" firstName={{ label: "NameFields / first", value: "—" }} lastName={{ label: "NameFields / last", value: "" }} error="error" invalid="lastName" readOnly />
          </div>
        </section>

        {/* Gli avvisi usano il `ToastProvider` della radice del router, come ogni altra pagina. */}
        <section className="g-styleguide__section" aria-labelledby="styleguide-dialogs">
          <h2 id="styleguide-dialogs">{t("styleguide.dialogs")}</h2>
          <DialogDemos />
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-fields">
          <h2 id="styleguide-fields">{t("styleguide.fields")}</h2>
          <FieldDemos />
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-tables">
          <h2 id="styleguide-tables">{t("styleguide.tables")}</h2>
          <TableDemo />
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-states">
          <h2 id="styleguide-states">{t("styleguide.states")}</h2>
          <div className="g-styleguide__row">
            {pillDemos.map(({ tone, icon }) => (
              <StatusPill key={tone} tone={tone} icon={icon}>{`StatusPill / ${tone}`}</StatusPill>
            ))}
          </div>
          <div className="g-styleguide__row">
            {tones.map((tone) => (
              <Chip key={tone} tone={tone}>{`Chip / ${tone}`}</Chip>
            ))}
            <Chip icon="lock">Chip / icon</Chip>
          </div>
          <Reason>Reason / info: il motivo per cui un controllo è spento, sempre scritto.</Reason>
          <Reason tone="warning">Reason / warning: manca il fuso orario dell&apos;azienda.</Reason>
          <AboutBox title="A cosa serve">
            <p>Qui c&apos;è chi fa parte dell&apos;Academy. Da qui si invita una persona nuova e si cambia ogni cosa dalla sua riga.</p>
            <h2>Chi decide</h2>
            <p>Il Super admin, l&apos;Admin e il Super user dell&apos;azienda. Nessuno cambia chi ha un ruolo più alto del suo.</p>
          </AboutBox>
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-feedback">
          <h2 id="styleguide-feedback">{t("styleguide.feedback")}</h2>
          <ToastButtons />
          <div className="g-styleguide__static">
            <SaveBar summary="2 modifiche non salvate al ruolo «Setter»: «Può chiamare» e «Vede i report»." onDiscard={() => {}} onSave={() => {}} saveLabel="Salva il ruolo" saving={false} />
            <SaveBar summary="1 modifica da salvare." problem="Scegli almeno un giorno lavorativo." onDiscard={() => {}} onSave={() => {}} saveLabel="Salva" saving={false} />
            <SaveBar summary="2 modifiche non salvate al ruolo «Setter»." onDiscard={() => {}} onSave={() => {}} saveLabel="Salva il ruolo" saving />
          </div>
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-navigation">
          <h2 id="styleguide-navigation">{t("styleguide.navigation")}</h2>
          <PageHeader eyebrow="Impostazioni · Academy" title="Team" actions={<Button variant="primary">Invita una persona</Button>} />
          <SegmentedDemo />
        </section>
      </Card>
    </PublicShell>
  );
}
